import { Injectable } from '@angular/core';

import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

import { AuthSession } from '../../core/interfaces';

import type { SurveyAnswer } from '@the-hive-frontend/libs';

@Injectable({ providedIn: 'root' })
export class StorageManager {
  private readonly storageKey = 'beeking.auth.session';
  private readonly clientSelectionKey = 'beeking.client.selection';
  private readonly pushTokenKey = 'beeking.push.token';
  private readonly pushEnabledKey = 'beeking.push.enabled';
  private readonly activeTenantKey = 'beeking.tenant.active';

  async getSession(): Promise<AuthSession | null> {
    try {
      const { value } = await SecureStoragePlugin.get({ key: this.storageKey });
      return JSON.parse(value) as AuthSession;
    } catch {
      return null;
    }
  }

  async setSession(session: AuthSession): Promise<void> {
    await SecureStoragePlugin.set({ key: this.storageKey, value: JSON.stringify(session) });
  }

  async clearSession(): Promise<void> {
    await SecureStoragePlugin.remove({ key: this.storageKey });
  }

  async getClientSelection(): Promise<string[] | null> {
    try {
      const { value } = await SecureStoragePlugin.get({ key: this.clientSelectionKey });
      return parseClientSelection(value);
    } catch {
      return null;
    }
  }

  async setClientSelection(uuids: readonly string[]): Promise<void> {
    await SecureStoragePlugin.set({
      key: this.clientSelectionKey,
      value: JSON.stringify(uuids),
    });
  }

  async clearClientSelection(): Promise<void> {
    await SecureStoragePlugin.remove({ key: this.clientSelectionKey });
  }

  async getActiveTenantUuid(): Promise<string | null> {
    try {
      const { value } = await SecureStoragePlugin.get({ key: this.activeTenantKey });
      return value.length > 0 ? value : null;
    } catch {
      return null;
    }
  }

  async setActiveTenantUuid(uuid: string): Promise<void> {
    await SecureStoragePlugin.set({ key: this.activeTenantKey, value: uuid });
  }

  async clearActiveTenantUuid(): Promise<void> {
    try {
      await SecureStoragePlugin.remove({ key: this.activeTenantKey });
    } catch {
      // ignore if key doesn't exist
    }
  }

  async getPushToken(): Promise<string | null> {
    try {
      const { value } = await SecureStoragePlugin.get({ key: this.pushTokenKey });
      return value;
    } catch {
      return null;
    }
  }

  async setPushToken(token: string): Promise<void> {
    await SecureStoragePlugin.set({ key: this.pushTokenKey, value: token });
  }

  async clearPushToken(): Promise<void> {
    try {
      await SecureStoragePlugin.remove({ key: this.pushTokenKey });
    } catch {
      // ignore if key doesn't exist
    }
  }

  async getPushEnabled(): Promise<boolean> {
    try {
      const { value } = await SecureStoragePlugin.get({ key: this.pushEnabledKey });
      return value !== 'false';
    } catch {
      return true;
    }
  }

  async setPushEnabled(enabled: boolean): Promise<void> {
    await SecureStoragePlugin.set({
      key: this.pushEnabledKey,
      value: String(enabled),
    });
  }

  async getSurveyDraft(formUuid: string, actionUuid: string): Promise<SurveyAnswer[] | null> {
    try {
      const key = buildSurveyDraftKey(formUuid, actionUuid);
      const { value } = await SecureStoragePlugin.get({ key });
      return JSON.parse(value) as SurveyAnswer[];
    } catch {
      return null;
    }
  }

  async setSurveyDraft(
    formUuid: string,
    actionUuid: string,
    answers: SurveyAnswer[]
  ): Promise<void> {
    const key = buildSurveyDraftKey(formUuid, actionUuid);
    await SecureStoragePlugin.set({ key, value: JSON.stringify(answers) });
  }

  async clearSurveyDraft(formUuid: string, actionUuid: string): Promise<void> {
    try {
      const key = buildSurveyDraftKey(formUuid, actionUuid);
      await SecureStoragePlugin.remove({ key });
    } catch {
      // ignore if key doesn't exist
    }
  }
}

function buildSurveyDraftKey(formUuid: string, actionUuid: string): string {
  return `beeking.survey.draft.${formUuid}.${actionUuid}`;
}

function parseClientSelection(value: string): string[] | null {
  const parsed = JSON.parse(value) as unknown;
  if (!Array.isArray(parsed)) {
    return null;
  }

  return parsed.filter((item): item is string => typeof item === 'string' && item.length > 0);
}
